import React, { useMemo } from 'react';
import './JungleBackground.css';

const Vine = React.memo(({ x, delay, length }: { 
  x: number; 
  delay: number;
  length: number;
}) => (
  <div 
    className="jungle-vine"
    style={{
      '--vine-x': x,
      '--vine-length': length,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const Leaf = React.memo(({ x, y, delay, size }: { 
  x: number; 
  y: number;
  delay: number;
  size: number;
}) => (
  <div 
    className="jungle-leaf"
    style={{
      '--leaf-x': x,
      '--leaf-y': y,
      '--leaf-size': size,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const TreePlatform = React.memo(({ x, y, width }: { 
  x: number; 
  y: number;
  width: number;
}) => (
  <div 
    className="tree-platform"
    style={{
      left: `${x}%`,
      bottom: `${y}%`,
      width: `${width}%`
    }}
  />
));

const JungleBackground = React.memo(() => {
  const vines = useMemo(() => 
    Array.from({ length: 8 }).map((_, i) => ({
      id: i,
      x: 15 + i * 10 + Math.random() * 8,
      delay: Math.random() * 6,
      length: 0.6 + Math.random() * 0.8
    })),
  []);

  const leaves = useMemo(() => 
    Array.from({ length: 15 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 60 + Math.random() * 40,
      delay: Math.random() * 10,
      size: 0.8 + Math.random() * 0.6
    })),
  []);

  const platforms = useMemo(() => 
    Array.from({ length: 4 }).map((_, i) => ({
      id: i,
      x: 10 + i * 20 + Math.random() * 10,
      y: 20 + i * 10 + Math.random() * 15,
      width: 15 + Math.random() * 10
    })),
  []);

  return (
    <div className="jungle-background">
      {/* Sky gradient */}
      <div className="jungle-sky" />
      
      {/* Canopy layers */}
      <div className="jungle-canopy">
        <div className="canopy-layer canopy-back" />
        <div className="canopy-layer canopy-mid" />
        <div className="canopy-layer canopy-front" />
      </div>
      
      {/* Tree trunks */}
      <div className="jungle-trunks">
        <div className="tree-trunk trunk-1" />
        <div className="tree-trunk trunk-2" />
        <div className="tree-trunk trunk-3" />
        <div className="tree-trunk trunk-4" />
      </div>
      
      {/* Tree platforms */}
      <div className="platforms-layer">
        {platforms.map(platform => (
          <TreePlatform
            key={platform.id}
            x={platform.x}
            y={platform.y}
            width={platform.width}
          />
        ))}
      </div>
      
      {/* Vines */}
      <div className="vines-layer">
        {vines.map(vine => (
          <Vine
            key={vine.id}
            x={vine.x}
            delay={vine.delay}
            length={vine.length}
          />
        ))}
      </div>
      
      {/* Leaves falling */}
      <div className="leaves-layer">
        {leaves.map(leaf => (
          <Leaf
            key={leaf.id}
            x={leaf.x}
            y={leaf.y}
            delay={leaf.delay}
            size={leaf.size}
          />
        ))}
      </div>
      
      {/* Ground vegetation */}
      <div className="jungle-ground" />
      <div className="jungle-vegetation" />
      
      {/* Light filtering through */}
      <div className="jungle-light-rays">
        <div className="light-ray ray-1" />
        <div className="light-ray ray-2" />
        <div className="light-ray ray-3" />
      </div>
      
      {/* Ambient particles */}
      <div className="jungle-particles" />
    </div>
  );
});

export default JungleBackground;